import { isEqual } from 'lodash';
import { type MutableRefObject, useRef } from 'react';
import { v4 as generateUuid } from 'uuid';

export interface AlgorithmParams<Payload> {
  payload: Payload;
  route: string;
  prevPayloadRef: MutableRefObject<Payload | undefined>;
  idempotencyKeyRef: MutableRefObject<string | undefined>;
}

// Generate an idempotency key once, reuse it until component unmount.
export function staticKeyAlgorithm<Payload>({
  idempotencyKeyRef,
}: AlgorithmParams<Payload>): string {
  if (!idempotencyKeyRef.current) return generateIdempotencyKey(idempotencyKeyRef);

  return idempotencyKeyRef.current;
}

// Same as staticKeyAlgorithm, but generate a new key when the request payload changes.
export function dynamicKeyAlgorithm<Payload>({
  payload,
  prevPayloadRef,
  idempotencyKeyRef,
}: AlgorithmParams<Payload>): string {
  if (!idempotencyKeyRef.current) return generateIdempotencyKey(idempotencyKeyRef);

  if (!isEqual(payload, prevPayloadRef.current)) {
    generateIdempotencyKey(idempotencyKeyRef);
    prevPayloadRef.current = payload;
  }

  return idempotencyKeyRef.current;
}

// Generate an idempotency key from the route and explicit key parts.
// The key is deterministic and will be the same across page refreshes for identical inputs.
export function deterministicKeyAlgorithm<Payload>(
  keyParts: (string | number)[]
): (params: AlgorithmParams<Payload>) => string {
  return ({ route }) => [route, ...keyParts].join('_');
}

export const useIdempotencyRefs = <T>() => {
  const prevPayloadRef = useRef<T | undefined>(undefined);
  const idempotencyKeyRef = useRef<string | undefined>(undefined);

  return { prevPayloadRef, idempotencyKeyRef };
};

function generateIdempotencyKey(idempotencyKeyRef: MutableRefObject<string | undefined>): string {
  idempotencyKeyRef.current = generateUuid();
  return idempotencyKeyRef.current;
}
